import * as THREE from "three";

export function generateNetworkId() {
  return Math.random().toString(36).slice(2, 10);
}

export class ScriptableObjects {
  static _registry = new WeakMap();

  static _factory = new Map();

  static registerType(type, factory) {
    if (!type || typeof factory !== "function") return;
    this._factory.set(type, factory);
  }

  static _wrapObject(obj) {
    if (!obj) return obj;

    if (this._registry.has(obj)) {
      const entry = this._registry.get(obj);
      return entry ? entry : obj;
    }

    const scriptableType =
      obj?.scriptableType || obj?.constructor?.scriptableType || obj?.constructor?.prototype?.scriptableType;

    const factory = this._factory.get(scriptableType);

    if (factory) {
      const wrapper = factory(obj);
      this._registry.set(obj, wrapper);
      this._registry.set(wrapper, obj);
      return wrapper;
    }

    return obj;
  }
}

// eslint-disable-next-line no-use-before-define
export class ScriptableObject extends ScriptableObjectMixin(THREE.Object3D, "ScriptableObject") {}

export function ScriptableObjectMixin(Object3DClass, className = null) {
  const MixedClass = class extends Object3DClass {
    static runtimeMode = "editor";

    static editor = null;

    static mediaInflator = null;

    static setRuntimeMode(mode) {
      if (mode === "aframe" || mode === "editor") {
        this.runtimeMode = mode;
      }
    }

    static setEditor(editor) {
      this.editor = editor;
    }

    static setMediaInflator(inflator) {
      this.mediaInflator = inflator;
    }

    constructor(...args) {
      super(...args);

      this.isScriptableObject = true;
      this._transformSyncSetup = false;
      this._forwardedChildren = new Set();
      this._logicalChildren = this.children || [];
      this.children = [];

      // Ensure parent accessor returns wrapped ScriptableObject when available
      try {
        this._scriptableParentRef = this.parent || null;
        Object.defineProperty(this, "parent", {
          get: () => {
            const p = this._scriptableParentRef;
            if (!p) return p;
            const wrapped = ScriptableObjects._wrapObject(p);
            return wrapped || p;
          },
          set: v => {
            this._scriptableParentRef = v;
          },
          enumerable: true,
          configurable: true
        });
      } catch (e) {
        /* no-op: parent accessor may already be defined */
      }

      if (ScriptableObject.runtimeMode === "aframe" && this.el) {
        this._setupTransformSync();
      }
    }

    _scriptableName = "";

    get name() {
      // Remove this fallback when all constructors are updated
      return this._scriptableName ? this._scriptableName : this.el?.object3D?.name;
    }

    set name(value) {
      this._scriptableName = value;
      if (ScriptableObject.runtimeMode === "aframe" && this.el?.object3D) {
        this.el.object3D.name = value;
      }
    }

    get children() {
      if (ScriptableObject.runtimeMode === "aframe") {
        const logicalChildren = this._logicalChildren || [];
        const forwardedChildren = this._forwardedChildren ? Array.from(this._forwardedChildren) : [];
        return [...logicalChildren, ...forwardedChildren];
      }

      return this._logicalChildren;
    }

    set children(value) {
      if (!Array.isArray(value)) {
        return;
      }

      if (ScriptableObject.runtimeMode === "aframe") {
        const allCurrentChildren = this.children;
        if (allCurrentChildren.length > 0) this.remove(...allCurrentChildren);
        if (value.length > 0) this.add(...value);
      } else {
        this._logicalChildren = value;
      }
    }

    _setupTransformSync() {
      if (this._transformSyncSetup) return;
      this._transformSyncSetup = true;

      if (!this.position) {
        this.position = new THREE.Vector3();
      }
      if (!this.quaternion) {
        this.quaternion = new THREE.Quaternion();
      }
      if (!this.scale) {
        this.scale = new THREE.Vector3(1, 1, 1);
      }
      if (!this.rotation) {
        this.rotation = new THREE.Euler();
      }

      if (this.el && this.el.object3D) {
        const sourceTransform = this.el.object3D;
        this.position.copy(sourceTransform.position);
        this.quaternion.copy(sourceTransform.quaternion);
        this.scale.copy(sourceTransform.scale);
      }

      this._updateElTransform = () => {
        if (!this.el || !this.el.object3D) return;

        const obj3D = this.el.object3D;

        if (this.position && obj3D.position) {
          obj3D.position.copy(this.position);
        }
        if (this.quaternion && obj3D.quaternion) {
          obj3D.quaternion.copy(this.quaternion);
        }
        if (this.scale && obj3D.scale) {
          obj3D.scale.copy(this.scale);
        }

        if (typeof this.el.setAttribute === "function") {
          if (this.position) {
            const p = this.position;
            this.el.setAttribute("position", `${p.x} ${p.y} ${p.z}`);
          }

          if (this.quaternion || this.rotation) {
            const euler = this.rotation ? this.rotation : new THREE.Euler().setFromQuaternion(this.quaternion, "XYZ");
            const rx = THREE.MathUtils.radToDeg(euler.x);
            const ry = THREE.MathUtils.radToDeg(euler.y);
            const rz = THREE.MathUtils.radToDeg(euler.z);
            this.el.setAttribute("rotation", `${rx} ${ry} ${rz}`);
          }

          if (this.scale) {
            const s = this.scale;
            this.el.setAttribute("scale", `${s.x} ${s.y} ${s.z}`);
          }
        }
      };

      const makeReactive = (obj, properties) => {
        if (!obj) return;
        properties.forEach(prop => {
          if (!(prop in obj)) return;
          let internal = obj[prop];
          Object.defineProperty(obj, prop, {
            get() {
              return internal;
            },
            set: newVal => {
              if (internal === newVal) return;
              internal = newVal;
              this._updateElTransform();
            }
          });
        });
      };

      this._updateElTransform();

      if (this.el && !this.el.object3D) {
        const onLoaded = () => {
          this._updateElTransform();
          this.el.removeEventListener("loaded", onLoaded);
        };

        if (this.el.hasLoaded) {
          this._updateElTransform();
        } else {
          this.el.addEventListener("loaded", onLoaded);
        }
      }

      const bindChange = obj => {
        if (!obj) return;
        if (typeof obj.onChange === "function") {
          obj.onChange(this._updateElTransform);
        }
        if (typeof obj.set === "function") {
          const originalSet = obj.set.bind(obj);
          obj.set = (...args) => {
            const result = originalSet(...args);
            this._updateElTransform();
            return result;
          };
        }
      };

      if (this.position) bindChange(this.position);
      if (this.quaternion) bindChange(this.quaternion);
      if (this.scale) bindChange(this.scale);
      if (this.rotation) bindChange(this.rotation);

      if (this.position) makeReactive(this.position, ["x", "y", "z"]);
      if (this.scale) makeReactive(this.scale, ["x", "y", "z"]);

      if (this.rotation) makeReactive(this.rotation, ["x", "y", "z"]);
      if (this.quaternion) makeReactive(this.quaternion, ["x", "y", "z", "w"]);
    }

    get el() {
      return this._el;
    }

    set el(value) {
      this._el = value;
      if (value) {
        this._setupTransformSync();
      }
    }

    getObjectByProperty(propertyName, value) {
      const searchRoot = ScriptableObject.runtimeMode === "aframe" && this.el?.object3D ? this.el.object3D : this;

      const findRecursively = currentObject => {
        const wrapper = ScriptableObjects._registry.get(currentObject);

        if (wrapper && wrapper.isScriptableObject) {
          if (wrapper[propertyName] === value) {
            return wrapper;
          }
        }

        if (currentObject[propertyName] === value) {
          return wrapper || ScriptableObjects._wrapObject(currentObject) || currentObject;
        }

        for (const child of currentObject.children) {
          const result = findRecursively(child);
          if (result) {
            return result;
          }
        }

        return null;
      };

      return findRecursively(searchRoot) || undefined;
    }

    getObjectById(id) {
      return this.getObjectByProperty("id", id);
    }

    getObjectByName(name) {
      return this.getObjectByProperty("name", name);
    }

    traverse(callback) {
      if (typeof callback !== "function") return;

      if (ScriptableObject.runtimeMode !== "aframe") {
        return super.traverse(callback);
      }

      const renderRoot = this.el?.object3D;
      if (!renderRoot) return;

      const visit = renderNode => {
        const maybeWrapper = ScriptableObjects._wrapObject(renderNode);
        const hasWrapper = !!(maybeWrapper && maybeWrapper.isScriptableObject);

        if (hasWrapper) {
          console.log("has wrapper", maybeWrapper);
          callback(maybeWrapper);
        }

        if (!hasWrapper || maybeWrapper !== renderNode) {
          callback(renderNode);
        }

        const children = renderNode.children ? renderNode.children.slice() : [];
        for (let i = 0; i < children.length; i++) {
          console.log("traverse", children[i]);
          visit(children[i]);
        }
      };

      visit(renderRoot);
    }

    traverseVisible(callback) {
      if (typeof callback !== "function") return;

      if (ScriptableObject.runtimeMode === "aframe") {
        const renderRoot = this.el?.object3D;
        if (renderRoot && typeof renderRoot.traverseVisible === "function") {
          renderRoot.traverseVisible(callback);
        }
      } else {
        super.traverseVisible(callback);
      }
    }

    traverseAncestors(callback) {
      if (typeof callback !== "function") return;

      if (ScriptableObject.runtimeMode === "aframe") {
        const renderObject = this.el?.object3D;

        if (renderObject && renderObject.parent) {
          let parent = renderObject.parent;
          while (parent) {
            callback(parent);
            parent = parent.parent;
          }
        }
      } else {
        super.traverseAncestors(callback);
      }
    }

    traverseScriptables(callback) {
      if (typeof callback !== "function") return;

      const renderRoot = ScriptableObject.runtimeMode === "aframe" && this.el?.object3D ? this.el.object3D : this;

      if (renderRoot && typeof renderRoot.traverse === "function") {
        renderRoot.traverse(node => {
          const wrapper = ScriptableObjects._wrapObject(node);

          if (wrapper && wrapper.isScriptableObject) {
            callback(wrapper);
          }
        });
      }
    }

    traverseVisibleScriptables(callback) {
      if (typeof callback !== "function") return;

      const renderRoot = ScriptableObject.runtimeMode === "aframe" && this.el?.object3D ? this.el.object3D : this;

      if (renderRoot && typeof renderRoot.traverseVisible === "function") {
        renderRoot.traverseVisible(renderNode => {
          const wrapper = ScriptableObjects._wrapObject(renderNode);

          if (wrapper && wrapper.isScriptableObject) {
            callback(wrapper);
          }
        });
      }
    }

    traverseAncestorsScriptables(callback) {
      if (typeof callback !== "function") return;

      const renderRoot = ScriptableObject.runtimeMode === "aframe" && this.el?.object3D ? this.el.object3D : this;

      if (renderRoot) {
        let parent = renderRoot.parent;
        while (parent) {
          const wrapper = ScriptableObjects._wrapObject(parent);

          if (wrapper && wrapper.isScriptableObject) {
            callback(wrapper);
          }

          parent = parent.parent;
        }
      }
    }

    dispose() {
      if (this.parent) {
        this.parent.remove(this);
      }
    }

    add(...objects) {
      if (this.constructor.runtimeMode !== "aframe") {
        return super.add(...objects);
      }

      console.log("add", objects);

      objects.forEach(obj => {
        if (!obj || !obj.isScriptableObject) {
          this.el?.object3D?.add(obj);
          this._forwardedChildren.add(obj);
          return;
        }

        const childWrapper = obj;
        const oldLogicalParent = childWrapper.parent;
        const childRenderObject = childWrapper.el?.object3D;

        // --- Step 1: Handle Initial Creation (No existing parent) ---
        if (!oldLogicalParent) {
          if (childWrapper.el && !childWrapper.el.parentElement) {
            this.el.appendChild(obj.el);
          }
        }

        // --- Step 2: RENDER WORLD Reparenting ---
        if (childRenderObject && this.el?.object3D) {
          this.el.object3D.add(childRenderObject);
        }

        // --- Step 3: LOGICAL WORLD Synchronization ---
        // a) Detach from the old logical parent's children list.
        if (oldLogicalParent && oldLogicalParent._logicalChildren) {
          const index = oldLogicalParent._logicalChildren.indexOf(childWrapper);
          if (index !== -1) {
            oldLogicalParent._logicalChildren.splice(index, 1);
          }
        }

        // b) Attach to the new logical parent ('this').
        childWrapper.parent = this;
        this._logicalChildren.push(childWrapper);
      });

      return this;
    }

    attach(obj) {
      if (!obj) return this;

      if (this.constructor.runtimeMode !== "aframe") {
        return super.attach(obj);
      }

      if (!obj.isScriptableObject || !obj.el) {
        this.el?.object3D?.attach(obj);
        return this;
      }

      const childWrapper = obj;

      // --- Step 1: Cache the World Transform ---
      childWrapper.el.object3D.updateWorldMatrix(true, false);
      const savedWorldMatrix = childWrapper.el.object3D.matrixWorld.clone();

      // --- Step 2: Perform the Reparenting using our own Robust `add` ---
      this.add(childWrapper);

      // --- Step 3: Restore the World Transform ---
      this.el.object3D.updateWorldMatrix(true, false);

      const invParentWorldMatrix = new THREE.Matrix4().copy(this.el.object3D.matrixWorld).invert();
      const newLocalMatrix = new THREE.Matrix4().multiplyMatrices(invParentWorldMatrix, savedWorldMatrix);
      newLocalMatrix.decompose(childWrapper.position, childWrapper.quaternion, childWrapper.scale);

      return this;
    }

    remove(...objects) {
      if (this.constructor.runtimeMode !== "aframe") {
        return super.remove(...objects);
      }

      objects.forEach(obj => {
        if (!obj) return;

        // Raw THREE children
        if (!obj.isScriptableObject) {
          this.el?.object3D?.remove(obj);
          this._forwardedChildren?.delete?.(obj);
          return;
        }

        const childWrapper = obj;

        // 1) Render graph detach (even if DOM is elsewhere)
        const ro = childWrapper.el?.object3D;
        if (ro && ro.parent) {
          ro.parent.remove(ro);
        }

        // 2) Logical unlink
        const i = this._logicalChildren.indexOf(childWrapper);
        if (i !== -1) {
          childWrapper.parent = null; // uses your safe setter
          this._logicalChildren.splice(i, 1);
        }

        // 3) DOM detach only if we actually own it (or you can be stricter: p === this.el)
        if (childWrapper.el) {
          const p = childWrapper.el.parentElement;
          if (p && (p === this.el || this.el?.contains(childWrapper.el))) {
            p.removeChild(childWrapper.el);
          }
        }
      });

      return this;
    }

    removeFromParent() {
      if (ScriptableObject.runtimeMode === "aframe") {
        const parent = ScriptableObjects._wrapObject(this.parent);

        if (parent !== null) {
          parent.remove(this);
        }

        return this;
      }

      return super.removeFromParent();
    }
  };

  if (className) {
    Object.defineProperty(MixedClass, "name", {
      value: className,
      configurable: true
    });
  }

  return MixedClass;
}

ScriptableObject.mediaFrameEcsInit = null;
ScriptableObject.setMediaFrameEcsInit = function (fn) {
  this.mediaFrameEcsInit = typeof fn === "function" ? fn : null;
};

ScriptableObject.snapColliderEcsInit = null;
ScriptableObject.setSnapColliderEcsInit = function (fn) {
  this.snapColliderEcsInit = typeof fn === "function" ? fn : null;
};

ScriptableObject.mediaFrameEcsAccess = null;
ScriptableObject.setMediaFrameEcsAccess = function (access) {
  this.mediaFrameEcsAccess = access && typeof access === "object" ? access : null;
};

export function patchedSceneAddFactory(scene, { sceneEl } = {}) {
  const originalAdd = scene.add.bind(scene);

  const isAFrame = ScriptableObject.runtimeMode === "aframe";

  const rootEl = sceneEl || scene._aframeRootEl;
  const scriptingRoot = scene._scriptingRoot || null;

  return function patchedSceneAdd(...objects) {
    if (isAFrame) {
      objects.forEach(obj => {
        if (!obj) return;

        if (!obj.isScriptableObject) {
          originalAdd(obj);
          return;
        }

        if (obj.el) {
          rootEl?.appendChild(obj.el);
        }

        if (scriptingRoot) {
          scriptingRoot.add(obj);
        }

        return;
      });
      return scene;
    }

    return originalAdd(...objects);
  };
}

export function patchedSceneAttachFactory(scene, { sceneEl } = {}) {
  const originalAttach = scene.attach.bind(scene);

  const isAFrame = ScriptableObject.runtimeMode === "aframe";

  const rootEl = sceneEl || scene._aframeRootEl;
  const scriptingRoot = scene._scriptingRoot || null;

  return function patchedSceneAttach(obj) {
    if (!obj) return scene;

    if (!isAFrame || !obj.isScriptableObject) {
      originalAttach(obj);
      return scene;
    }

    const childWrapper = obj;

    if (!childWrapper.el || !childWrapper.el.object3D) {
      originalAttach(childWrapper);
      return scene;
    }

    childWrapper.el.object3D.updateWorldMatrix(true, false);
    const savedWorldMatrix = childWrapper.el.object3D.matrixWorld.clone();

    const oldLogicalParent = childWrapper.parent;
    if (!oldLogicalParent && childWrapper.el && !childWrapper.el.parentElement && rootEl) {
      rootEl.appendChild(childWrapper.el);
    }

    if (scriptingRoot && typeof scriptingRoot.add === "function") {
      scriptingRoot.add(childWrapper);
    } else {
      originalAttach(childWrapper);
    }

    const parentRenderObject =
      (scriptingRoot && scriptingRoot.el && scriptingRoot.el.object3D) || (rootEl && rootEl.object3D) || scene;

    if (parentRenderObject && typeof parentRenderObject.updateWorldMatrix === "function") {
      parentRenderObject.updateWorldMatrix(true, false);
    }

    const invParentWorldMatrix = new THREE.Matrix4().copy(parentRenderObject.matrixWorld).invert();
    const newLocalMatrix = new THREE.Matrix4().multiplyMatrices(invParentWorldMatrix, savedWorldMatrix);
    newLocalMatrix.decompose(childWrapper.position, childWrapper.quaternion, childWrapper.scale);

    return scene;
  };
}

export function patchedSceneRemoveFactory(scene, { sceneEl } = {}) {
  const originalRemove = scene.remove.bind(scene);
  const isAFrame = ScriptableObject.runtimeMode === "aframe";
  const rootEl = sceneEl || scene._aframeRootEl;
  const scriptingRoot = scene._scriptingRoot || null;

  return function patchedSceneRemove(...objects) {
    if (!isAFrame) {
      originalRemove(...objects);
      return scene;
    }

    objects.forEach(obj => {
      if (!obj) return;

      if (!obj.isScriptableObject) {
        originalRemove(obj);
        return;
      }

      const wrapper = obj;

      // Render detach
      const renderObject = wrapper.el?.object3D;
      if (renderObject && renderObject.parent) renderObject.parent.remove(renderObject);

      // Logical detach
      if (scriptingRoot && typeof scriptingRoot.remove === "function") {
        scriptingRoot.remove(wrapper);
      } else {
        // fallback to wrapper’s own logic
        wrapper.removeFromParent?.();
      }

      // DOM detach (only if under the scene root)
      if (rootEl && wrapper.el && rootEl.contains(wrapper.el)) {
        rootEl.removeChild(wrapper.el);
      }
    });

    return scene;
  };
}

export function createGlobalAddPatcher(root) {
  const originalObject3DAdd = THREE.Object3D.prototype.add;

  return function patchedGlobalAdd(...objects) {
    const parentObject = this;

    const parentEl = parentObject.el || root;

    objects.forEach(obj => {
      if (!obj || !obj.isScriptableObject) {
        originalObject3DAdd.call(parentObject, obj);
        return;
      }

      const childWrapper = obj;

      const parentWrapper = ScriptableObjects._wrapObject(parentObject);

      if (childWrapper.el && parentEl && childWrapper.el.parentElement !== parentEl) {
        parentEl.appendChild(childWrapper.el);
      }

      if (parentWrapper && parentWrapper.isScriptableObject) {
        parentWrapper.add(childWrapper, { _domAlreadyHandled: true });
      }
    });

    return this;
  };
}

export function createGlobalRemovePatcher(root) {
  const originalObject3DRemove = THREE.Object3D.prototype.remove;

  return function patchedGlobalRemove(...objects) {
    const parentObject = this;
    const parentEl = parentObject.el || root;

    objects.forEach(obj => {
      if (!obj || !obj.isScriptableObject) {
        originalObject3DRemove.call(parentObject, obj);
        return;
      }

      const childWrapper = obj;
      const parentWrapper = ScriptableObjects._wrapObject(parentObject);

      // If parent is a wrapper, delegate to its aligned remove()
      if (parentWrapper && parentWrapper.isScriptableObject) {
        parentWrapper.remove(childWrapper);
        return;
      }

      // Otherwise, perform a safe best-effort detach:
      // 1) Render
      const renderObject = childWrapper.el?.object3D;
      if (renderObject && renderObject.parent) renderObject.parent.remove(renderObject);

      // 2) DOM (if this raw parent has an associated root)
      if (childWrapper.el && parentEl && parentEl.contains(childWrapper.el)) {
        parentEl.removeChild(childWrapper.el);
      }

      // 3) Logical
      const logicalParent = childWrapper.parent;
      if (logicalParent && logicalParent === parentWrapper) {
        childWrapper.parent = null;
        const arr = logicalParent?._logicalChildren;
        if (Array.isArray(arr)) {
          const i = arr.indexOf(childWrapper);
          if (i !== -1) arr.splice(i, 1);
        }
      }
    });

    return this;
  };
}

export function patchedSceneTraverseFactory(scene, { sceneEl } = {}) {
  const originalTraverse = scene.traverse.bind(scene);
  const isAFrame = ScriptableObject.runtimeMode === "aframe";
  const rootEl = sceneEl || scene._aframeRootEl;

  function patchedSceneTraverse(callback) {
    if (typeof callback !== "function") return scene;

    if (!isAFrame) {
      originalTraverse(callback);
      return scene;
    }

    const renderRoot = (rootEl && rootEl.object3D) || scene;
    if (!renderRoot) return scene;

    // Use Three's own traversal, but expand each visit into (wrapper?, render)
    renderRoot.traverse(node => {
      const maybeWrapper = ScriptableObjects._wrapObject(node);
      const hasWrapper = !!(maybeWrapper && maybeWrapper.isScriptableObject);

      if (hasWrapper) callback(maybeWrapper);
      if (!hasWrapper || maybeWrapper !== node) callback(node);
    });

    return scene;
  }

  return patchedSceneTraverse;
}